import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Activity, RefreshCw, CheckCircle2, AlertTriangle, Bug, ShieldCheck, FileCode2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import {
  getAnalyticsHealth,
  recordSentryTestException,
  getSentrySymbolication,
  type AnalyticsHealth,
  type SentrySymbolication,
  runLiveTelemetryCheck,
  type LiveTelemetryCheck,
} from "@/lib/integration-health.functions";
import { isPosthogReady, isSentryReady, sendSentryTestException, track } from "@/lib/analytics";

export const Route = createFileRoute("/analytics-health")({
  component: AnalyticsHealthPage,
  head: () => ({
    meta: [
      { title: "Analytics Health - Inquira Admin" },
      { name: "description", content: "Admin-only health check for PostHog analytics and Sentry error monitoring." },
      { property: "og:title", content: "Analytics Health - Inquira Admin" },
      { property: "og:description", content: "Admin-only health check for PostHog analytics and Sentry error monitoring." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
});

function Pill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        ok ? "bg-primary/15 text-primary" : "bg-destructive/15 text-destructive"
      }`}
    >
      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
      {label}
    </span>
  );
}

function AnalyticsHealthPage() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [health, setHealth] = useState<AnalyticsHealth | null>(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sourcemaps, setSourcemaps] = useState<SentrySymbolication | null>(null);
  const [smChecking, setSmChecking] = useState(false);
  const [live, setLive] = useState<LiveTelemetryCheck | null>(null);
  const [liveRunning, setLiveRunning] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    (async () => {
      const { data: roleOk } = await supabase.rpc("has_role" as never, {
        _user_id: user.id,
        _role: "super_admin",
      } as never);
      setIsAdmin(roleOk === true);
      setChecking(false);
    })();
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAnalyticsHealth();
      setHealth(data as AnalyticsHealth);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load analytics health");
    } finally {
      setLoading(false);
    }
  }, []);

  const checkSourcemaps = useCallback(async (eventId?: string) => {
    setSmChecking(true);
    try {
      const res = await getSentrySymbolication({ data: eventId ? { eventId } : {} });
      setSourcemaps(res as SentrySymbolication);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Source map check failed");
    } finally {
      setSmChecking(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  useEffect(() => {
    if (isAdmin) void checkSourcemaps();
  }, [isAdmin, checkSourcemaps]);

  const sendPosthogTestEvent = async () => {
    await track("analytics_health_probe", { at: new Date().toISOString() });
    toast.success("Test event sent to PostHog. Re-check in ~30s.");
  };

  const fireSentryTest = async () => {
    setTesting(true);
    try {
      const eventId = sendSentryTestException();
      await recordSentryTestException({ data: { eventId: eventId ?? undefined } });
      if (eventId) toast.success(`Sentry test exception sent (${eventId.slice(0, 8)}…)`);
      else toast.error("Sentry is not initialized in this environment.");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to send test exception");
    } finally {
      setTesting(false);
    }
  };

  const runProductionCheck = async () => {
    setLiveRunning(true);
    try {
      const res = (await runLiveTelemetryCheck()) as LiveTelemetryCheck;
      setLive(res);
      if (res.ok) toast.success("PostHog and Sentry both accepted a live event.");
      else toast.error("At least one provider did not accept the event — see details.");
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Production check failed");
    } finally {
      setLiveRunning(false);
    }
  };

  if (authLoading || checking) {
    return <div className="flex min-h-screen items-center justify-center text-muted-foreground">Loading…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldCheck className="h-10 w-10 text-muted-foreground" />
        <h1 className="text-xl font-semibold text-foreground">Admins only</h1>
        <p className="text-sm text-muted-foreground">This analytics health page is restricted to super admins.</p>
        <Link to="/" className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
          Go home
        </Link>
      </div>
    );
  }

  const ph = health?.posthog;
  const sn = health?.sentry;

  return (
    <div className="min-h-screen bg-background px-4 py-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Admin
          </Link>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-foreground disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Re-check
          </button>
        </div>

        <header className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
            <Activity className="h-6 w-6 text-primary" /> Analytics health
          </h1>
          <p className="text-sm text-muted-foreground">
            Live status of PostHog event ingestion and Sentry error capture
            {health ? ` · checked ${new Date(health.checkedAt).toLocaleString()}` : ""}.
          </p>
        </header>

        {/* Post-publish live verification */}
        <section className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-foreground">Post-publish verification</h2>
            <div className="flex flex-wrap gap-2">
              <Pill ok={!!live?.posthog.accepted} label={live ? (live.posthog.accepted ? "PostHog event landed" : "PostHog rejected") : "PostHog not checked"} />
              <Pill ok={!!live?.sentry.accepted} label={live ? (live.sentry.accepted ? "Sentry event landed" : "Sentry rejected") : "Sentry not checked"} />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Sends one real event to PostHog and one real event to Sentry from the deployed server and confirms both
            providers accepted it. Run this once after every publish.
          </p>
          <button
            onClick={() => void runProductionCheck()}
            disabled={liveRunning}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            <ShieldCheck className={`h-4 w-4 ${liveRunning ? "animate-pulse" : ""}`} /> Run production check
          </button>
          {live && (
            <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
              <div><dt className="text-muted-foreground">Environment</dt><dd className="font-semibold text-foreground">{live.environment}</dd></div>
              <div><dt className="text-muted-foreground">PostHog status</dt><dd className="font-semibold text-foreground">{live.posthog.status ?? "—"}</dd></div>
              <div><dt className="text-muted-foreground">Sentry status</dt><dd className="font-semibold text-foreground">{live.sentry.status ?? "—"}</dd></div>
              <div><dt className="text-muted-foreground">Sentry event id</dt><dd className="truncate font-mono text-xs font-semibold text-foreground">{live.sentry.eventId?.slice(0, 12) ?? "—"}</dd></div>
            </dl>
          )}
          {live && (live.posthog.detail || live.sentry.detail) && (
            <div className="space-y-1 text-xs text-destructive">
              {live.posthog.detail && <p>PostHog: {live.posthog.detail}</p>}
              {live.sentry.detail && <p>Sentry: {live.sentry.detail}</p>}
            </div>
          )}
          {live && (
            <p className="text-xs text-muted-foreground">Checked {new Date(live.checkedAt).toLocaleString()}</p>
          )}
        </section>

        {/* PostHog */}
        <section className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-foreground">PostHog</h2>
            <div className="flex gap-2">
              <Pill ok={!!ph?.configured} label={ph?.configured ? "Key configured" : "Key missing"} />
              <Pill ok={!!ph?.ingestOk} label={ph?.ingestOk ? "Ingest reachable" : `Ingest ${ph?.ingestStatus ?? "unreachable"}`} />
              <Pill ok={isPosthogReady()} label={isPosthogReady() ? "SDK live" : "SDK idle"} />
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div><dt className="text-muted-foreground">Events / 1h</dt><dd className="font-semibold text-foreground">{ph?.eventsLastHour ?? "—"}</dd></div>
            <div><dt className="text-muted-foreground">Events / 24h</dt><dd className="font-semibold text-foreground">{ph?.eventsLast24h ?? "—"}</dd></div>
            <div><dt className="text-muted-foreground">Last event</dt><dd className="font-semibold text-foreground">{ph?.lastEventAt ? new Date(ph.lastEventAt).toLocaleTimeString() : "none"}</dd></div>
            <div><dt className="text-muted-foreground">Failures / 24h</dt><dd className={`font-semibold ${(ph?.failuresLast24h ?? 0) > 0 ? "text-destructive" : "text-foreground"}`}>{ph?.failuresLast24h ?? 0}</dd></div>
          </dl>
          <button
            onClick={() => void sendPosthogTestEvent()}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            <Activity className="h-4 w-4" /> Send test event
          </button>
          <p className="text-xs text-muted-foreground">Host: {ph?.host ?? "—"}</p>
        </section>

        {/* Sentry */}
        <section className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold text-foreground">Sentry</h2>
            <div className="flex gap-2">
              <Pill ok={!!sn?.configured} label={sn?.configured ? "DSN configured" : "DSN missing"} />
              <Pill ok={isSentryReady()} label={isSentryReady() ? "SDK live" : "SDK idle"} />
              <Pill ok={true} label="Runtime tracking only" />
            </div>
          </div>
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div><dt className="text-muted-foreground">Test exceptions / 24h</dt><dd className="font-semibold text-foreground">{sn?.testExceptionsLast24h ?? 0}</dd></div>
            <div><dt className="text-muted-foreground">Last test</dt><dd className="font-semibold text-foreground">{sn?.lastTestExceptionAt ? new Date(sn.lastTestExceptionAt).toLocaleTimeString() : "none"}</dd></div>
            <div><dt className="text-muted-foreground">Failures / 24h</dt><dd className={`font-semibold ${(sn?.failuresLast24h ?? 0) > 0 ? "text-destructive" : "text-foreground"}`}>{sn?.failuresLast24h ?? 0}</dd></div>
            <div><dt className="text-muted-foreground">Ingest host</dt><dd className="truncate font-semibold text-foreground">{sn?.dsnHost ?? "—"}</dd></div>
          </dl>
          <button
            onClick={() => void fireSentryTest()}
            disabled={testing}
            className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            <Bug className="h-4 w-4" /> Capture test exception
          </button>
        </section>

        {/* Source map symbolication */}
        <section className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-foreground">
              <FileCode2 className="h-5 w-5 text-primary" /> Source map symbolication
            </h2>
            <div className="flex flex-wrap gap-2">
              <Pill ok={true} label="Uploads disabled" />
              <Pill
                ok={true}
                label="No build secret required"
              />
            </div>
          </div>
          <p className="text-sm text-muted-foreground">
            Runtime Sentry capture is active through VITE_SENTRY_DSN. Automatic source-map upload and
            symbolication verification are intentionally disabled until an external secure build pipeline is configured.
          </p>
          <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
            <div><dt className="text-muted-foreground">Frames symbolicated</dt><dd className="font-semibold text-foreground">{sourcemaps ? `${sourcemaps.symbolicatedFrames}/${sourcemaps.totalInAppFrames}` : "—"}</dd></div>
            <div><dt className="text-muted-foreground">Minified frames</dt><dd className={`font-semibold ${(sourcemaps?.minifiedFrames ?? 0) > 0 ? "text-destructive" : "text-foreground"}`}>{sourcemaps?.minifiedFrames ?? "—"}</dd></div>
            <div><dt className="text-muted-foreground">Release</dt><dd className="truncate font-semibold text-foreground">—</dd></div>
            <div><dt className="text-muted-foreground">Artifacts uploaded</dt><dd className="font-semibold text-foreground">{sourcemaps?.artifactsUploaded ?? "—"}</dd></div>
          </dl>
          {sourcemaps?.reason && (
            <p className={`text-sm ${sourcemaps.ok ? "text-muted-foreground" : "text-destructive"}`}>{sourcemaps.reason}</p>
          )}
          {(sourcemaps?.frames?.length ?? 0) > 0 && (
            <div className="overflow-x-auto rounded-lg border border-border/60">
              <table className="w-full text-left text-xs">
                <thead className="text-[11px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2">File</th>
                    <th className="px-3 py-2">Function</th>
                    <th className="px-3 py-2">Line</th>
                    <th className="px-3 py-2">Mapped</th>
                  </tr>
                </thead>
                <tbody>
                  {sourcemaps!.frames.map((f, i) => (
                    <tr key={`${f.filename}-${i}`} className="border-t border-border/60">
                      <td className="max-w-[260px] truncate px-3 py-1.5 font-mono text-foreground">{f.filename ?? "—"}</td>
                      <td className="px-3 py-1.5 font-mono text-muted-foreground">{f.function ?? "—"}</td>
                      <td className="px-3 py-1.5 text-muted-foreground">{f.lineno ?? "—"}:{f.colno ?? "—"}</td>
                      <td className={`px-3 py-1.5 font-semibold ${f.symbolicated ? "text-primary" : "text-destructive"}`}>
                        {f.symbolicated ? "yes" : "no"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => void checkSourcemaps()}
              disabled={smChecking}
              className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm text-foreground disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${smChecking ? "animate-spin" : ""}`} /> Refresh status
            </button>
            {sourcemaps?.eventUrl && (
              <a href={sourcemaps.eventUrl} target="_blank" rel="noreferrer" className="text-sm text-primary underline">
                Open event in Sentry
              </a>
            )}
          </div>
        </section>

        {/* Failure log */}
        <section className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-3 text-lg font-semibold text-foreground">Recent integration events</h2>
          {(health?.recentFailures?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No PostHog or Sentry failures recorded. 🎉</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">When</th>
                    <th className="py-2 pr-3">Source</th>
                    <th className="py-2 pr-3">Kind</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2">Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {health!.recentFailures.map((row) => (
                    <tr key={row.id} className="border-t border-border/60">
                      <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">{new Date(row.created_at).toLocaleString()}</td>
                      <td className="py-2 pr-3 font-medium text-foreground">{row.source}</td>
                      <td className="py-2 pr-3">{row.kind}</td>
                      <td className="py-2 pr-3">{row.status_code ?? "—"}</td>
                      <td className="py-2 text-muted-foreground">{row.detail ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
